import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SLUG_DE_TO_EN, SLUG_EN_TO_DE, USECASES } from "./usecases";
import { HOME_SEO_V4 } from "./home_copy";

const BRAND = "BERLIN-CUBE";

const H1_REST = {
  de: "Klimaangepasste modulare Stadtinfrastruktur mit DEKRA-geprüftem Zufahrtsschutz für den Schutz öffentlicher Räume.",
  en: "Climate-adapted modular urban infrastructure with DEKRA-tested vehicle access protection for safe public spaces.",
};
const DEFINITION = {
  de: "Berlin-Cube ist ein modulares Stadtinfrastruktursystem aus Berlin, das begrünte Stadtmöbel mit DEKRA-geprüftem Zufahrtsschutz nach IWA 14-1:2013 verbindet — für Innenstädte, Plätze, Fußgängerzonen und Veranstaltungen.",
  en: "Berlin-Cube is a modular urban infrastructure system from Berlin that combines planted street furniture with DEKRA-tested vehicle access protection to IWA 14-1:2013 — for city centres, squares, pedestrian zones and events.",
};

const dict = {
  de: {
    brand: BRAND,
    nav: {
      vision: "Vision", platform: "Platform", collection: "Collection",
      berlin: "Berlin", tech: "Technology", finance: "Finance", contact: "Kontakt",
      challenges: "Herausforderungen", impact: "Impact", realisiert: "Realisiert", presse: "Presse", anwendungen: "Anwendungen", aktuelles: "Aktuelles",
      cta: "Gespräch anfragen", pdf: "Konzept", langLabel: "Sprache",
    },
    hero: {
      brandLine1: "BERLIN",
      brandLine2: "CUBE",
      h1Rest: H1_REST.de,
      definition: DEFINITION.de,
      positioning: "Klimaangepasste Stadtinfrastruktur",
      claim: "STADT. SICHER. GRÜN. SCHÖN.",
      claimDe: "Sicherheit muss man nicht sehen. Man soll sie erleben.",
      leadSpaces: "Stadtinfrastruktur für sichere und lebenswerte öffentliche Räume. Stadtgrün. Klimaanpassung. Aufenthalt. Smart City. Geprüfter Zufahrtsschutz.",
      lead: "Berlin-Cube entwickelt Infrastruktur, die Städte sicherer, grüner und lebenswerter macht.",
      cta1: "Vision entdecken",
      cta2: "Konzept herunterladen",
      scroll: "Scrollen",
    },
    change: {
      kicker: "01 — Der Kontext",
      h1: "Städte verändern sich.",
      h2: "Nicht morgen. Heute.",
      body: "Klimawandel, neue Mobilität, steigende Sicherheitsanforderungen und der Wunsch nach lebenswerten öffentlichen Räumen verändern die Anforderungen an urbane Infrastruktur.",
      quote: "Die Frage ist nicht mehr, ob Infrastruktur sich verändern muss.",
      quoteBold: "Sondern wie.",
    },
    why: {
      kicker: "02 — Die Herausforderungen",
      title: "Die Städte von morgen brauchen Infrastruktur, die mehr kann.",
      sub: "Sieben Themen. Eine Antwort.",
      items: [
        { t: "Sicherheit", d: "Öffentliche Räume geschützt gestalten." },
        { t: "Hitze", d: "Städtische Wärmeinseln kühlen." },
        { t: "Starkregen", d: "Wasser aufnehmen, zurückhalten, verdunsten." },
        { t: "Mobilität", d: "Wege für Menschen, Räder, E-Verkehr." },
        { t: "Energie", d: "Dezentral erzeugen, speichern, verteilen." },
        { t: "Digitalisierung", d: "Sensorik, Konnektivität, Datenknoten." },
        { t: "Aufenthaltsqualität", d: "Räume, in denen Menschen bleiben." },
      ],
    },
    challenges: {
      kicker: "02 — Herausforderungen",
      title: "Welche Herausforderung möchten Sie lösen?",
      sub: "Nicht Produkte. Sondern die Fragen, mit denen Städte heute konfrontiert sind.",
      items: [
        { t: "Klimaanpassung", d: "Städte auf ein Klima vorbereiten, das sich schon heute verändert.", n: "01" },
        { t: "Hitze", d: "Wärmeinseln kühlen. Schatten. Verdunstung. Aufenthalt im Sommer.", n: "02" },
        { t: "Starkregen", d: "Wasser aufnehmen, zurückhalten, verzögert abgeben — dort, wo es fällt.", n: "03" },
        { t: "Stadtgrün", d: "Bepflanzung, Habitate, Biodiversität. Grün als Infrastruktur, nicht als Dekoration.", n: "04" },
        { t: "Aufenthaltsqualität", d: "Orte, an denen Menschen nicht nur durchgehen, sondern bleiben.", n: "05" },
        { t: "Mobilität", d: "Wege für Menschen, Fahrräder, E-Verkehr. Ladepunkte, wo sie gebraucht werden.", n: "06" },
        { t: "Smart City", d: "Sensorik, Konnektivität, Datenknoten. Verlässlich. Sicher. Souverän.", n: "07" },
        { t: "Sicherheit", d: "Öffentliche Räume geschützt gestalten — ohne sie in Festungen zu verwandeln.", n: "08" },
        { t: "Finanzierung", d: "Ein System, das kommunale Investitionen, Förderung und Refinanzierung verbindet.", n: "09" },
      ],
    },
    impact: {
      kicker: "03 — Impact",
      title: "Welche Ziele unterstützt Berlin-Cube?",
      sub: "Zehn politische Ziele. Eine Infrastruktur, die auf sie einzahlt.",
      note: "Impact-Matrix · politische Zielbilder, keine Fördermittelzuordnung.",
      items: [
        "Klimaanpassung",
        "Resilienz",
        "Biodiversität",
        "Smart City",
        "Mobilität",
        "Innere Sicherheit",
        "Digitalisierung",
        "Barrierefreiheit",
        "Stadtentwicklung",
        "Aufenthaltsqualität",
      ],
    },
    line: {
      kicker: "04 — The Line",
      title: "The Line connects.",
      body: "Die Linie im Namen Berlin-Cube steht für Verbindung. Sie verbindet Module, Funktionen und Stadträume zu einem intelligenten Gesamtsystem.",
      body2: "Nicht ein einzelnes Element steht im Mittelpunkt. Sondern das Zusammenspiel.",
      words: ["Safety", "Climate", "Energy", "Mobility", "Smart City", "Quality of Life"],
    },
    platform: {
      kicker: "05 — Die Plattform",
      title: "Eine Plattform. Viele Lösungen.",
      body: "Berlin-Cube verbindet Sicherheit, Klimaanpassung, Energie, Digitalisierung und Aufenthaltsqualität in einer modularen Infrastrukturplattform. Jede Stadt kann die Module kombinieren, die sie wirklich braucht.",
      domains: [
        { t: "Infrastructure", d: "Geprüftes Grundsystem. Modulare Verbindungen. Öffentlicher Raum." },
        { t: "Finance", d: "Kommunal, Förderprogramme, Public-Private-Partnerships, Sponsoring." },
        { t: "Care", d: "Betrieb, Wartung, Ersatzteile. Ein Ansprechpartner." },
        { t: "Energy", d: "Solar, Speicher, Ladepunkte. Dezentral gedacht." },
        { t: "Media", d: "Informations- und Medienflächen im Stadtraum." },
      ],
    },
    collection: {
      kicker: "04 — Die Plattform · Collection",
      line: "Ein Grundsystem. Unterschiedliche Funktionen. Unterschiedliche Orte.",
      title: "The Collection.",
      sub: "Neun Module. Ein System.",
      intro:
        "Berlin-Cube verbindet Sicherheit, Klimaanpassung, Energie, Digitalisierung und Aufenthaltsqualität in einer modularen Plattform. Nicht ein einzelnes Modul steht im Mittelpunkt — sondern das Zusammenspiel.",
      chips: ["Infrastructure", "Finance", "Care", "Energy", "Media"],
      items: [
        { t: "Protection", d: "DEKRA-Crashtest nach IWA 14-1:2013 · Prüfbericht Nr. 201844624." },
        { t: "Green", d: "Bepflanzung, Habitat, Kühlung." },
        { t: "Water", d: "Regenwasser speichern, Verdunstung, Bewässerung." },
        { t: "Solar", d: "PV-Baldachin mit adaptiver Verschattung." },
        { t: "Smart", d: "Sensorik, Wi-Fi, Umweltdaten." },
        { t: "Media", d: "Digitale Info- und Medienflächen im Stadtraum." },
        { t: "Mobility", d: "E-Bike, Scooter und Ladepunkte." },
        { t: "Lounge", d: "Sitz-, Aufenthalts- und Schattenmodule." },
        { t: "Art", d: "Kuratierte Kunst im öffentlichen Raum." },
      ],
    },
    vision: {
      kicker: "06 — Vision Berlin",
      title: "Eine Vision für den öffentlichen Raum von morgen.",
      body: "Die Visualisierungen zeigen exemplarisch, wie modulare Infrastruktur Sicherheit, Begrünung und Aufenthaltsqualität in einem Berliner Stadtraum verbinden könnte.",
      body2: "Sie dienen der Veranschaulichung eines Zukunftskonzepts und stellen keine beschlossene Planung dar.",
      caption: "Berlin · Kantstraße · Übergang zum Breitscheidplatz",
      note: "Konzeptvisualisierung",
    },
    tech: {
      kicker: "07 — Technologie",
      title: "Technologie.",
      sub: "Saubere Trennung. Keine unbelegten Aussagen.",
      provenTitle: "Nachgewiesen",
      proven: [
        { t: "DEKRA", d: "Unabhängige Prüfung durch DEKRA — dokumentiert." },
        { t: "DEKRA-Crashtest", d: "IWA 14-1:2013 · Testfahrzeug 7.114 kg · 63,9 km/h · Prüfbericht Nr. 201844624." },
        { t: "IWA 14-1:2013", d: "Internationaler Prüfstandard für Fahrzeugsperren." },
      ],
      dev: [
        { t: "Generation 2", d: "Nächste Baureihe — höhere Sicherheitsreserven als Ziel." },
        { t: "Neue Module", d: "Water, Solar, Mobility, Media, Smart in Entwicklung." },
        { t: "Neue Funktionen", d: "Sensorik, Speicher, adaptive Systeme — im Aufbau." },
      ],
      devTitle: "In Entwicklung",
    },
    financing: {
      kicker: "08 — Finanzierung",
      title: "Finanzierung.",
      body: "Berlin-Cube verbindet mehrere Finanzierungsbausteine. Nicht als Versprechen, dass sich jedes Projekt selbst finanziert — sondern als Werkzeugkasten.",
      flow: [
        { t: "Kommunale Investition", d: "Die Grundlage — Haushalte, Sondervermögen, Investitionsprogramme." },
        { t: "Förderprogramme", d: "EU, Bund, Länder — Sicherheit, Klima, Digital." },
        { t: "Sponsoring", d: "Standortbezogene Partnerschaften mit klarer Governance." },
        { t: "Werbeflächen", d: "Digitale Medien- und Informationsflächen im Stadtraum." },
        { t: "Servicegesellschaft", d: "Ein Vertragspartner statt fragmentierter Zuständigkeiten." },
        { t: "Pflege", d: "Kontinuierlicher Betrieb, Wartung und Ersatzteile." },
        { t: "Modernisierung", d: "Nachrüstung, neue Module, neue Funktionen im Lebenszyklus." },
      ],
      note: "Je nach Standort können einzelne Bausteine unterschiedlich gewichtet werden. Kein Baustein steht für sich allein.",
      funding: {
        kicker: "Förderfähigkeit",
        title: "Wofür Städte heute Förderung erhalten",
        cards: [
          { t: "Klimaanpassung & Hitzevorsorge", d: "CO₂-Bindung, Verschattung, Kühlung." },
          { t: "Schwammstadt & Starkregenvorsorge", d: "Regenwasser speichern und nutzen." },
          { t: "Stadtgrün & Biodiversität", d: "Bepflanzung, Habitate, urbane Ökologie." },
          { t: "Smart City & Digitalisierung", d: "Sensorik, Daten, Konnektivität." },
          { t: "Ladeinfrastruktur & Mobilität", d: "E-Bike, Ladepunkte, Mikromobilität." },
          { t: "Innere Sicherheit & Zufahrtsschutz", d: "Geprüfter Anprallschutz." },
        ],
        outro: "Welche Programme im Einzelfall greifen, ist projektbezogen zu prüfen — wir unterstützen dabei.",
        guideLine: "Förderleitfaden für Kommunen — welche Programme für welchen Baustein infrage kommen, kompakt aufbereitet.",
        guideCta: "Förderleitfaden anfordern",
        guideMessage: "Bitte senden Sie mir den Förderleitfaden für Kommunen.",
      },
    },
    europe: {
      kicker: "09 — Europa",
      title: "Designed in Berlin.\nBuilt for Europe.",
      body: "Berlin ist der Ursprung. Europa die Vision.",
      note: "Als Perspektive — nicht als Versprechen.",
      cities: ["Berlin", "Paris", "Wien", "Amsterdam", "Kopenhagen", "Barcelona", "Warschau", "Zürich"],
    },
    contact: {
      kicker: "10 — Kontakt",
      title: "Let's build better cities together.",
      body: "Berlin-Cube entwickelt Infrastruktur, die Städte sicherer, grüner und lebenswerter macht.",
      cta1: "Gespräch anfragen",
      cta2: "Konzept herunterladen",
      form: {
        name: "Name", email: "E-Mail", org: "Organisation / Kommune", role: "Funktion (optional)", message: "Ihre Nachricht",
        submit: "Gespräch anfragen", sending: "Wird gesendet…",
        success: "Vielen Dank. Wir melden uns kurzfristig.", error: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
      },
    },
    footer: {
      claim: "Protecting Cities. Enriching Life.",
      mission: "Berlin-Cube entwickelt Infrastruktur, die Städte sicherer, grüner und lebenswerter macht.",
      legal: "DEKRA-Crashtest nach IWA 14-1:2013 · Testfahrzeug 7.114 kg · 63,9 km/h · DEKRA-Prüfbericht Nr. 201844624 · Europäisches Patent EP 3 486 374",
      copyright: "© 2026 BERLIN-CUBE. Alle Rechte vorbehalten.",
    },
  },
  en: {
    brand: BRAND,
    nav: {
      vision: "Vision", platform: "Platform", collection: "Collection",
      berlin: "Berlin", tech: "Technology", finance: "Finance", contact: "Contact",
      challenges: "Challenges", impact: "Impact", realisiert: "Built", presse: "Press", anwendungen: "Use cases", aktuelles: "News",
      cta: "Request meeting", pdf: "Concept", langLabel: "Language",
    },
    hero: {
      brandLine1: "BERLIN",
      brandLine2: "CUBE",
      h1Rest: H1_REST.en,
      definition: DEFINITION.en,
      positioning: "The Future of Urban Infrastructure",
      claim: "Protecting Cities. Enriching Life.",
      claimDe: "Sicherheit muss man nicht sehen. Man soll sie erleben.",
      leadSpaces: "Urban infrastructure for safe and liveable public spaces. Urban greenery. Climate adaptation. Amenity. Smart City. Tested vehicle access protection.",
      lead: "Berlin-Cube exists to help cities become safer, greener and more livable.",
      cta1: "Discover the vision",
      cta2: "Download concept",
      scroll: "Scroll",
    },
    change: {
      kicker: "01 — The Context",
      h1: "Cities are changing.",
      h2: "Not tomorrow. Today.",
      body: "Climate change, new mobility, rising safety demands and the desire for liveable public spaces are transforming the requirements for urban infrastructure.",
      quote: "The question is no longer whether infrastructure needs to change.",
      quoteBold: "But how.",
    },
    why: {
      kicker: "02 — The Challenges",
      title: "Tomorrow's cities need infrastructure that does more.",
      sub: "Seven themes. One answer.",
      items: [
        { t: "Safety", d: "Protect public spaces." },
        { t: "Heat", d: "Cool urban heat islands." },
        { t: "Heavy rain", d: "Absorb, retain, evaporate water." },
        { t: "Mobility", d: "Space for people, bikes, e-transport." },
        { t: "Energy", d: "Generate, store, distribute — decentralised." },
        { t: "Digitalisation", d: "Sensors, connectivity, data nodes." },
        { t: "Amenity", d: "Places where people stay." },
      ],
    },
    challenges: {
      kicker: "02 — Challenges",
      title: "Which challenge would you like to solve?",
      sub: "Not products. The questions cities are facing today.",
      items: [
        { t: "Climate adaptation", d: "Prepare cities for a climate that is already changing.", n: "01" },
        { t: "Heat", d: "Cool urban heat islands. Shade. Evaporation. Summer amenity.", n: "02" },
        { t: "Heavy rain", d: "Absorb, retain and release water — where it falls.", n: "03" },
        { t: "Urban greenery", d: "Planting, habitats, biodiversity. Green as infrastructure, not decoration.", n: "04" },
        { t: "Amenity", d: "Places where people don't just pass through — they stay.", n: "05" },
        { t: "Mobility", d: "Space for people, bikes, e-transport. Charging where it's needed.", n: "06" },
        { t: "Smart City", d: "Sensors, connectivity, data nodes. Reliable. Secure. Sovereign.", n: "07" },
        { t: "Safety", d: "Protect public spaces — without turning them into fortresses.", n: "08" },
        { t: "Financing", d: "A system that combines municipal investment, subsidies and refinancing.", n: "09" },
      ],
    },
    impact: {
      kicker: "03 — Impact",
      title: "Which goals does Berlin-Cube support?",
      sub: "Ten political goals. One infrastructure contributing to all of them.",
      note: "Impact matrix · political target picture, not a funding-programme mapping.",
      items: [
        "Climate adaptation",
        "Resilience",
        "Biodiversity",
        "Smart City",
        "Mobility",
        "Public safety",
        "Digitalisation",
        "Accessibility",
        "Urban development",
        "Amenity",
      ],
    },
    line: {
      kicker: "04 — The Line",
      title: "The Line connects.",
      body: "The line in the name Berlin-Cube stands for connection. It links modules, functions and urban spaces into one intelligent whole.",
      body2: "Not one element takes the spotlight. The interplay does.",
      words: ["Safety", "Climate", "Energy", "Mobility", "Smart City", "Quality of Life"],
    },
    platform: {
      kicker: "05 — The Platform",
      title: "One platform. Many solutions.",
      body: "Berlin-Cube combines safety, climate adaptation, energy, digitalisation and amenity in one modular infrastructure platform. Every city can combine the modules it really needs.",
      domains: [
        { t: "Infrastructure", d: "Tested base system. Modular connections. Public space." },
        { t: "Finance", d: "Municipal, subsidies, PPPs, sponsoring." },
        { t: "Care", d: "Operation, maintenance, spare parts. One partner." },
        { t: "Energy", d: "Solar, storage, charging. Decentralised." },
        { t: "Media", d: "Information & media surfaces in the city." },
      ],
    },
    collection: {
      kicker: "04 — The Platform · Collection",
      line: "One base system. Different functions. Different places.",
      title: "The Collection.",
      sub: "Nine modules. One system.",
      intro:
        "Berlin-Cube combines safety, climate adaptation, energy, digitalisation and amenity in one modular platform. Not a single module takes the spotlight — the interplay does.",
      chips: ["Infrastructure", "Finance", "Care", "Energy", "Media"],
      items: [
        { t: "Protection", d: "DEKRA crash test to IWA 14-1:2013 · test report no. 201844624." },
        { t: "Green", d: "Planting, habitat, cooling." },
        { t: "Water", d: "Rainwater retention, evaporation, irrigation." },
        { t: "Solar", d: "PV canopy with adaptive shading." },
        { t: "Smart", d: "Sensors, Wi-Fi, environmental data." },
        { t: "Media", d: "Digital info & media surfaces in the urban realm." },
        { t: "Mobility", d: "E-bike, scooter, charging point." },
        { t: "Lounge", d: "Seating, amenity and shade modules." },
        { t: "Art", d: "Curated art in the public realm." },
      ],
    },
    vision: {
      kicker: "06 — Vision Berlin",
      title: "A vision for the public space of tomorrow.",
      body: "The visualisations illustrate how modular infrastructure could combine safety, greenery and amenity in a Berlin urban context.",
      body2: "They serve as illustrations of a future concept and do not represent adopted planning.",
      caption: "Berlin · Kantstraße · transition to Breitscheidplatz",
      note: "Concept visualisation",
    },
    tech: {
      kicker: "07 — Technology",
      title: "Technology.",
      sub: "Clean separation. No unproven claims.",
      provenTitle: "Proven",
      proven: [
        { t: "DEKRA", d: "Independent testing by DEKRA — documented." },
        { t: "DEKRA crash test", d: "IWA 14-1:2013 · test vehicle 7,114 kg · 63.9 km/h · test report no. 201844624." },
        { t: "IWA 14-1:2013", d: "International test standard for vehicle security barriers." },
      ],
      dev: [
        { t: "Generation 2", d: "Next series — higher safety reserves as target." },
        { t: "New modules", d: "Water, Solar, Mobility, Media, Smart in development." },
        { t: "New functions", d: "Sensors, storage, adaptive systems — under way." },
      ],
      devTitle: "In development",
    },
    financing: {
      kicker: "08 — Financing",
      title: "Financing.",
      body: "Berlin-Cube combines multiple financing components. Not as a promise that every project finances itself — but as a toolbox.",
      flow: [
        { t: "Municipal investment", d: "The foundation — budgets, special funds, investment programmes." },
        { t: "Subsidy programmes", d: "EU, federal, regional — safety, climate, digital." },
        { t: "Sponsoring", d: "Location-based partnerships with clear governance." },
        { t: "Advertising surfaces", d: "Digital media and information surfaces in the urban realm." },
        { t: "Service company", d: "One contract partner instead of fragmented responsibilities." },
        { t: "Care", d: "Continuous operation, maintenance and spare parts." },
        { t: "Modernisation", d: "Retrofits, new modules, new functions across the lifecycle." },
      ],
      note: "Depending on location, individual components can be weighted differently. No single component stands alone.",
      funding: {
        kicker: "Funding eligibility",
        title: "What cities receive funding for today",
        cards: [
          { t: "Climate adaptation & heat resilience", d: "CO₂ sequestration, shading, cooling." },
          { t: "Sponge city & stormwater management", d: "Capture and reuse rainwater." },
          { t: "Urban greening & biodiversity", d: "Planting, habitats, urban ecology." },
          { t: "Smart City & digitalisation", d: "Sensors, data, connectivity." },
          { t: "Charging infrastructure & mobility", d: "E-bike, charging points, micro-mobility." },
          { t: "Public safety & vehicle mitigation", d: "Tested impact protection." },
        ],
        outro: "Which programmes apply in each case must be assessed project-by-project — we support this.",
        guideLine: "Funding guide for municipalities — which programmes fit which building block, in brief.",
        guideCta: "Request funding guide",
        guideMessage: "Please send me the funding guide for municipalities.",
      },
    },
    europe: {
      kicker: "09 — Europe",
      title: "Designed in Berlin.\nBuilt for Europe.",
      body: "Berlin is the origin. Europe the vision.",
      note: "As a perspective — not a promise.",
      cities: ["Berlin", "Paris", "Vienna", "Amsterdam", "Copenhagen", "Barcelona", "Warsaw", "Zurich"],
    },
    contact: {
      kicker: "10 — Contact",
      title: "Let's build better cities together.",
      body: "Berlin-Cube exists to help cities become safer, greener and more livable.",
      cta1: "Request meeting",
      cta2: "Download concept",
      form: {
        name: "Name", email: "Email", org: "Organization / Municipality", role: "Role (optional)", message: "Your message",
        submit: "Request meeting", sending: "Sending…",
        success: "Thank you. We will get back to you shortly.", error: "Something went wrong. Please try again.",
      },
    },
    footer: {
      claim: "Protecting Cities. Enriching Life.",
      mission: "Berlin-Cube exists to help cities become safer, greener and more livable.",
      legal: "DEKRA crash test to IWA 14-1:2013 · test vehicle 7,114 kg · 63.9 km/h · DEKRA test report no. 201844624 · European patent EP 3 486 374",
      copyright: "© 2026 BERLIN-CUBE. All rights reserved.",
    },
  },
};

const LanguageContext = createContext({ lang: "de", setLang: () => {}, t: dict.de });

const BASE_URL = "https://berlin-cube.de";
export const LANGS = ["de", "en", "fr", "pl", "es", "uk", "it", "nl", "cs"];
export const LOCALES = { de: "de_DE", en: "en_GB", fr: "fr_FR", pl: "pl_PL", es: "es_ES", uk: "uk_UA", it: "it_IT", nl: "nl_NL", cs: "cs_CZ" };
export const homePath = (l) => (l === "de" ? "/" : `/${l}/`);
// Pfad einer Anwendungsseite je Sprache (fr/pl nur wo frSlug/plSlug gepflegt ist)
export const ucPath = (u, l) => (l === "de" ? `/${u.deSlug}` : l === "en" ? `/en/${u.enSlug}` : u[`${l}Slug`] ? `/${l}/${u[`${l}Slug`]}` : null);
const langFromPath = (p) => LANGS.find((l) => l !== "de" && (p === `/${l}` || p.startsWith(`/${l}/`))) || "de";
const isHomePath = (p) => p === "/" || LANGS.some((l) => p === `/${l}` || p === `/${l}/`);

export const HOME_SEO = {
  de: {
    title: "BERLIN-CUBE | Zufahrtsschutz, Stadtgrün & modulare Stadtinfrastruktur",
    description:
      "Berlin-Cube verbindet Stadtgrün, Klimaanpassung, Aufenthaltsqualität und DEKRA-geprüften Zufahrtsschutz in einem modularen System für den Schutz öffentlicher Räume.",
  },
  en: {
    title: "BERLIN-CUBE | Vehicle access protection, urban greenery & modular urban infrastructure",
    description:
      "Berlin-Cube combines urban greenery, climate adaptation, quality of stay and DEKRA-tested vehicle access protection in one modular system for the protection of public spaces.",
  },
};

export const applyHomeSeo = (lang) => {
  const seo = HOME_SEO_V4[lang] || HOME_SEO[lang] || HOME_SEO.de;
  document.title = seo.title;
  const desc = document.querySelector('meta[name="description"]');
  if (desc) desc.setAttribute("content", seo.description);
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute("href", `${BASE_URL}${homePath(lang)}`);
  // hreflang clientseitig wie im Prerender: alle neun Sprachen + x-default
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  [...LANGS.map((l) => [l, homePath(l)]), ["x-default", "/"]].forEach(([hl, p]) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate"); el.setAttribute("hreflang", hl); el.setAttribute("href", `${BASE_URL}${p}`);
    document.head.appendChild(el);
  });
};

// Muss innerhalb von <BrowserRouter> stehen: Sprache wird aus der Route abgeleitet.
export const LanguageProvider = ({ children }) => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  // Nur für Seiten ohne eigene Sprachroute (Impressum/Datenschutz): Umschalten DE/EN ohne Navigation
  const [override, setOverride] = useState(null);
  useEffect(() => { setOverride(null); }, [pathname]);
  const lang = override && !isHomePath(pathname) ? override : langFromPath(pathname);

  const setLang = useCallback((l) => {
    const go = (p) => navigate(p + hash);
    if (isHomePath(pathname)) { go(homePath(l)); return; }
    const clean = pathname.replace(/\/$/, "");
    // Anwendungsseiten: passende Sprachfassung, sonst Startseite der Sprache
    const uc = USECASES.find((u) => LANGS.some((x) => ucPath(u, x) === clean));
    if (uc) { go(ucPath(uc, l) || homePath(l)); return; }
    // Übrige Unterseiten gibt es nur DE/EN: andere Sprachen führen zur Startseite
    if (l !== "de" && l !== "en") { go(homePath(l)); return; }
    if (clean === "/aktuelles" && l === "en") { go("/en/news"); return; }
    if (clean === "/en/news" && l === "de") { go("/aktuelles"); return; }
    if (l === "en" && SLUG_DE_TO_EN[clean.slice(1)]) { go(`/en/${SLUG_DE_TO_EN[clean.slice(1)]}`); return; }
    if (l === "de" && clean.startsWith("/en/") && SLUG_EN_TO_DE[clean.slice(4)]) { go(`/${SLUG_EN_TO_DE[clean.slice(4)]}`); return; }
    if (clean === "/projekte" && l === "en") { go("/en/projects"); return; }
    if (clean === "/technologie" && l === "en") { go("/en/technology"); return; }
    if (clean === "/presse" && l === "en") { go("/en/press"); return; }
    if (clean === "/en/projects" && l === "de") { go("/projekte"); return; }
    if (clean === "/en/technology" && l === "de") { go("/technologie"); return; }
    if (clean === "/en/press" && l === "de") { go("/presse"); return; }
    setOverride(l);
  }, [pathname, hash, navigate]);

  // SEO: lang-Attribut, Canonical, Title/Description je Sprache
  useEffect(() => {
    document.documentElement.lang = lang;
    const home = isHomePath(pathname);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", home ? `${BASE_URL}${homePath(lang)}` : `${BASE_URL}${pathname}`);
    if (home) applyHomeSeo(lang);
  }, [lang, pathname]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: dict[lang] || dict.en }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
